import React, { useEffect, useRef, useState } from 'react';
import Seo from '../components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { Flame, CheckCircle2, Loader2, XCircle, ArrowRight, Music, Download } from 'lucide-react';
import { getPaymentStatus, API } from '../lib/apiClient';
import { useCart } from '../context/CartContext';

const POLL_MAX = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | pending | error
  const [downloads, setDownloads] = useState([]);
  const { clearCart } = useCart();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === 'paid') {
          setState('paid');
          setDownloads(data.downloads || []);
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (['failed', 'expired'].includes(data.payment_status)) {
          setState('error');
          return;
        }
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) {
          setState('pending');
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) {
          setState('error');
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen ember-texture flex items-center justify-center px-6 py-32">
      <Seo title="Order Confirmed" path="/payment/success" noindex />
      <div className="card-dark rounded-3xl p-10 md:p-14 max-w-lg w-full text-center">
        {state === 'polling' && (
          <>
            <Loader2 className="w-14 h-14 text-fire-gold mx-auto mb-6 animate-spin" strokeWidth={1.3} />
            <h1 className="font-display text-3xl text-fire-cream mb-2">Confirming your order…</h1>
            <p className="text-fire-cream/60 font-body">One moment while we confirm your payment.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-fire-gold mx-auto mb-6" strokeWidth={1.3} />
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">THANK YOU</span>
            <h1 className="font-display text-4xl text-fire-cream mt-3 mb-3">You wear your testimony now.</h1>
            <p className="text-fire-cream/65 font-body mb-8">
              Your order is confirmed. A confirmation will follow. Thank you for supporting the After the Fire™ movement.
            </p>
            {downloads.length > 0 && (
              <div className="text-left card-dark rounded-2xl border border-fire-gold/30 p-5 mb-8" data-testid="download-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Music className="w-4 h-4 text-fire-gold" />
                  <span className="font-marcellus tracking-[0.2em] text-fire-gold text-[11px] uppercase">Your Music Downloads</span>
                </div>
                <ul className="space-y-2">
                  {downloads.map((d) => (
                    <li key={d.token}>
                      <a
                        href={`${API}/download/${d.token}`}
                        className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 border border-fire-gold/25 hover:bg-fire-gold/10 transition-colors group"
                        data-testid={`download-link-${d.token}`}
                      >
                        <span className="font-cormorant text-lg text-fire-cream/90">{d.title}</span>
                        <span className="btn-gold rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
                          <Download className="w-3.5 h-3.5" /> Download
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-fire-cream/45 font-body text-[11px] mt-3">These links are yours to keep — save your files to your device.</p>
              </div>
            )}
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/shop" className="btn-gold rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 text-sm">
                Keep Shopping <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">Back Home</Link>
            </div>
          </>
        )}
        {state === 'pending' && (
          <>
            <Flame className="w-14 h-14 text-fire-gold mx-auto mb-6 animate-flicker" strokeWidth={1.3} />
            <h1 className="font-display text-3xl text-fire-cream mb-2">Almost there</h1>
            <p className="text-fire-cream/60 font-body mb-8">
              Your payment is still processing. It may take a moment — you’ll receive confirmation once it completes.
            </p>
            <Link to="/shop" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">Back to Shop</Link>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-14 h-14 text-fire-maroon mx-auto mb-6" strokeWidth={1.3} />
            <h1 className="font-display text-3xl text-fire-cream mb-2">We couldn’t confirm this</h1>
            <p className="text-fire-cream/60 font-body mb-8">
              We weren’t able to confirm your payment. If you were charged, please contact us and we’ll make it right.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/shop" className="btn-gold rounded-full px-7 py-3.5 font-semibold text-sm">Return to Shop</Link>
              <Link to="/contact" className="btn-outline-gold rounded-full px-7 py-3.5 font-semibold text-sm">Contact Us</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
